import { TreeView } from "@/components/tree-view";
import { Skeleton } from "@/components/ui/skeleton";
import { EntityCard, EntityCardSkeleton } from "@cohesive/ui-components";
import { useDebounce } from "@uidotdev/usehooks";
import {
  Clock,
  Folder as FolderIcon,
  Folders,
  Home as HomeIcon,
  StarIcon,
  Trash,
  Users,
} from "lucide-react";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { EntityDragRow } from "../../dnd/EntityDragRow";
import { BaseSelectorProps } from "../BaseSelector";

import type { LucideProps } from "lucide-react";

import type { TreeDataItem as TreeViewDataItem } from "@/components/tree-view";
import {
  GoogleDriveFileEntity,
  GoogleDriveFolderEntity,
} from "@cohesive/api-entities";
import { buildGenericById, toGenericArray } from "../../genericProjection";
import {
  Selector,
  SelectorResults,
  SelectorSearch,
  SelectorSidebar,
} from "../Selector";
import {
  useGoogleDriveFilesList,
  useGoogleDriveFilesListInfinite,
} from "./useGoogleDriveFilesList";
import { useGoogleDrivesList } from "./useGoogleDrivesList";

function isGoogleDriveFile(
  item: GoogleDriveFileEntity | GoogleDriveFolderEntity,
): item is GoogleDriveFileEntity {
  return item.base === "file";
}

function isGoogleDriveFolder(
  item: GoogleDriveFileEntity | GoogleDriveFolderEntity,
): item is GoogleDriveFolderEntity {
  return item.base === "file_folder";
}

type TreeSVGIcon = React.ForwardRefExoticComponent<
  Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
>;
interface TreeDataItem extends Omit<TreeViewDataItem, "icon"> {
  icon?: TreeSVGIcon | React.ReactNode;
  order_by?: string;
  query?: string[];
  folderID?: string | null;
}

export const DriveSelector: React.FC<BaseSelectorProps> = ({
  connection,
  onSelect,
}) => {
  const defaultMyDriveFolder: TreeDataItem = {
    id: "my-drive",
    folderID: "root",
    name: "My Drive",
    icon: HomeIcon,
    query: ["trashed = false"],
    order_by: "modifiedTime desc, name",
  };

  const [selectedFolder, setSelectedFolder] = useState<TreeDataItem | null>(
    defaultMyDriveFolder,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedQuery = useDebounce(searchQuery, 500);
  const parentRef = useRef<HTMLDivElement>(null);

  const rootFolderID = "root";

  const { data: allFoldersData, isLoading: isFoldersLoading } =
    useGoogleDriveFilesList(connection, {
      page_size: 1000,
      query:
        "mimeType='application/vnd.google-apps.folder' and trashed = false",
    });

  const { data: allDrivesData } = useGoogleDrivesList(connection);

  const allFolders = useMemo(() => {
    return allFoldersData?.data?.filter(isGoogleDriveFolder) || [];
  }, [allFoldersData]);

  const buildFolderTree = (
    folders: GoogleDriveFolderEntity[],
    parentId: string | null = null,
  ): TreeDataItem[] => {
    return folders
      .filter((f) => {
        if (parentId) {
          return f.data.parents?.find((p) => p.id === parentId);
        }

        return false;
      })
      .map((f) => {
        const item: TreeDataItem = {
          id: f.data.id,
          folderID: f.data.id,
          name: f.data.title,
          icon: FolderIcon,
          query: ["trashed = false"],
          order_by: "modifiedTime desc, name",
          children: buildFolderTree(folders, f.data.id),
          onClick: () => setSelectedFolder(item),
        };

        return item;
      });
  };

  const sidebarTreeData: TreeDataItem[] = useMemo(() => {
    const myDrive: TreeDataItem = {
      id: "my-drive",
      folderID: rootFolderID || "root",
      name: "My Drive",
      icon: HomeIcon,
      query: ["trashed = false"],
      order_by: "modifiedTime desc, name",
      children: buildFolderTree(allFolders, rootFolderID),
      onClick: () => setSelectedFolder(myDrive),
    };

    const recentItem: TreeDataItem = {
      id: "recents",
      folderID: rootFolderID || "root",
      name: "Recent",
      icon: Clock,
      query: ["trashed = false"],
      order_by: "viewedByMeTime desc, name",
      onClick: () => setSelectedFolder(recentItem),
    };

    const starredItem: TreeDataItem = {
      id: "starred",
      folderID: null,
      name: "Starred",
      icon: StarIcon,
      query: ["starred = true", "trashed = false"],
      order_by: "viewedByMeTime desc, name",
      onClick: () => setSelectedFolder(starredItem),
    };

    const trashedItem: TreeDataItem = {
      id: "trashed",
      folderID: null,
      name: "Trash",
      icon: Trash,
      query: ["trashed = true"],
      order_by: "viewedByMeTime desc, name",
      onClick: () => setSelectedFolder(trashedItem),
    };

    const sharedWithMeItem: TreeDataItem = {
      id: "shared-with-me",
      name: "Shared with me",
      icon: Users,
      query: ["sharedWithMe"],
      order_by: "sharedWithMeTime desc, name",
      onClick: () => setSelectedFolder(sharedWithMeItem),
    };

    const sharedDrives =
      allDrivesData?.data?.map((item) => {
        const folder = {
          id: item.data.id,
          folderID: item.data.id,
          name: item.data.title,
          icon: Folders,
          query: ["trashed = false"],
          order_by: "modifiedTime desc, name",
          children: buildFolderTree(allFolders, item.data.id),
          onClick: () => setSelectedFolder(folder),
        };

        return folder;
      }) || [];

    return [
      myDrive,
      sharedWithMeItem,
      ...sharedDrives,
      recentItem,
      starredItem,
      trashedItem,
    ];
  }, [allFolders, rootFolderID, allDrivesData]);

  const apiQuery = debouncedQuery
    ? `name contains '${debouncedQuery}'`
    : undefined;

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGoogleDriveFilesListInfinite(connection, {
      query: [apiQuery, ...(selectedFolder?.query || ["trashed = false"])]
        .filter(Boolean)
        .join(" and "),
      folder_id: selectedFolder?.folderID || "root",
      order_by: selectedFolder?.order_by,
      page_size: 20,
    });

  const items = data?.pages.flatMap((page) => page.data) || [];

  // Generic projections (entity_format=both) keyed by entity id — placed on the
  // canvas in place of the typed file/folder.
  const genericById = useMemo(
    () =>
      buildGenericById(
        data?.pages.flatMap((page) => toGenericArray(page.data_generic)) ?? [],
      ),
    [data],
  );

  // Handle scroll to load more
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      // Trigger next page when 80% scrolled for smoother prefetching
      if (
        scrollHeight - scrollTop <= clientHeight * 1.2 &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  // Handlers
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleRecordSelect = (
    item: GoogleDriveFileEntity | GoogleDriveFolderEntity,
  ) => {
    if (isGoogleDriveFolder(item)) {
      // Navigate into the folder
      setSelectedFolder({
        id: item.data.id,
        folderID: item.data.id,
        name: item.data.title,
        order_by: "modifiedTime desc, name",
        icon: FolderIcon,
      });
    } else if (isGoogleDriveFile(item)) {
      onSelect(genericById.get(item.data.id) ?? item);
    }
  };

  const handleMentionClick = (
    item: GoogleDriveFileEntity | GoogleDriveFolderEntity,
  ) => {
    // Add folder or file to chat
    onSelect(genericById.get(item.data.id) ?? item);
  };

  const itemIds = useMemo(
    () => items.filter((i) => i && i.data && i.data.id).map((i) => i.data.id),
    [items],
  );

  return (
    <Selector>
      <SelectorSidebar>
        {isFoldersLoading ? (
          <div className="p-2 space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        ) : (
          <TreeView
            data={sidebarTreeData}
            onSelectChange={(item) => setSelectedFolder(item || null)}
            expandAll={false}
          />
        )}
      </SelectorSidebar>
      <SelectorSearch
        value={searchQuery}
        onChange={handleSearchChange}
        placeholder="Search files and folders..."
        autoFocus
      />
      <SelectorResults
        ref={parentRef}
        onScroll={handleScroll}
        style={{ paddingBottom: "200px" }}
      >
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <EntityCardSkeleton
                key={i}
                className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
              />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <FolderIcon className="h-8 w-8 mb-2" />
            <p>No files or folders found</p>
          </div>
        ) : (
          <div className="divide-y">
            {items
              .filter((item) => item && item.data && item.data.id)
              .map((item) => (
                <EntityDragRow
                  key={item.data.id}
                  entity={item}
                  generic={genericById.get(item.data.id)}
                  entityId={item.data.id}
                  orderedIds={itemIds}
                  onSelect={() => handleRecordSelect(item)}
                  dragScope="google-drive"
                >
                  <EntityCard
                    entity={item}
                    variant="compact"
                    onMentionClick={() => handleMentionClick(item)}
                    className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
                  />
                </EntityDragRow>
              ))}
            {/* Loading indicator for next page */}
            {isFetchingNextPage && (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <EntityCardSkeleton
                    key={`loading-${i}`}
                    className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </SelectorResults>
    </Selector>
  );
};
